import { useState, useEffect } from 'react';
import { MapPin, Search, X, ChevronDown, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

// Popular expat cities for quick selection - comprehensive list
const POPULAR_CITIES = [
  // Europe
  { city: 'Budapest', country: 'Hungary' },
  { city: 'Prague', country: 'Czech Republic' },
  { city: 'Vienna', country: 'Austria' },
  { city: 'Berlin', country: 'Germany' },
  { city: 'Munich', country: 'Germany' },
  { city: 'Frankfurt', country: 'Germany' },
  { city: 'Hamburg', country: 'Germany' },
  { city: 'Düsseldorf', country: 'Germany' },
  { city: 'Cologne', country: 'Germany' },
  { city: 'Barcelona', country: 'Spain' },
  { city: 'Madrid', country: 'Spain' },
  { city: 'Valencia', country: 'Spain' },
  { city: 'Malaga', country: 'Spain' },
  { city: 'Seville', country: 'Spain' },
  { city: 'Lisbon', country: 'Portugal' },
  { city: 'Porto', country: 'Portugal' },
  { city: 'Amsterdam', country: 'Netherlands' },
  { city: 'Rotterdam', country: 'Netherlands' },
  { city: 'The Hague', country: 'Netherlands' },
  { city: 'Paris', country: 'France' },
  { city: 'Lyon', country: 'France' },
  { city: 'Nice', country: 'France' },
  { city: 'Marseille', country: 'France' },
  { city: 'London', country: 'United Kingdom' },
  { city: 'Manchester', country: 'United Kingdom' },
  { city: 'Edinburgh', country: 'United Kingdom' },
  { city: 'Birmingham', country: 'United Kingdom' },
  { city: 'Dublin', country: 'Ireland' },
  { city: 'Brussels', country: 'Belgium' },
  { city: 'Antwerp', country: 'Belgium' },
  { city: 'Zurich', country: 'Switzerland' },
  { city: 'Geneva', country: 'Switzerland' },
  { city: 'Basel', country: 'Switzerland' },
  { city: 'Milan', country: 'Italy' },
  { city: 'Rome', country: 'Italy' },
  { city: 'Florence', country: 'Italy' },
  { city: 'Venice', country: 'Italy' },
  { city: 'Naples', country: 'Italy' },
  { city: 'Stockholm', country: 'Sweden' },
  { city: 'Gothenburg', country: 'Sweden' },
  { city: 'Copenhagen', country: 'Denmark' },
  { city: 'Oslo', country: 'Norway' },
  { city: 'Helsinki', country: 'Finland' },
  { city: 'Warsaw', country: 'Poland' },
  { city: 'Krakow', country: 'Poland' },
  { city: 'Wroclaw', country: 'Poland' },
  { city: 'Bucharest', country: 'Romania' },
  { city: 'Sofia', country: 'Bulgaria' },
  { city: 'Athens', country: 'Greece' },
  { city: 'Thessaloniki', country: 'Greece' },
  { city: 'Bratislava', country: 'Slovakia' },
  { city: 'Ljubljana', country: 'Slovenia' },
  { city: 'Zagreb', country: 'Croatia' },
  { city: 'Belgrade', country: 'Serbia' },
  { city: 'Tallinn', country: 'Estonia' },
  { city: 'Riga', country: 'Latvia' },
  { city: 'Vilnius', country: 'Lithuania' },
  { city: 'Luxembourg City', country: 'Luxembourg' },
  { city: 'Monaco', country: 'Monaco' },
  { city: 'Reykjavik', country: 'Iceland' },
  
  // Middle East
  { city: 'Dubai', country: 'UAE' },
  { city: 'Abu Dhabi', country: 'UAE' },
  { city: 'Doha', country: 'Qatar' },
  { city: 'Riyadh', country: 'Saudi Arabia' },
  { city: 'Jeddah', country: 'Saudi Arabia' },
  { city: 'Kuwait City', country: 'Kuwait' },
  { city: 'Manama', country: 'Bahrain' },
  { city: 'Muscat', country: 'Oman' },
  { city: 'Tel Aviv', country: 'Israel' },
  { city: 'Jerusalem', country: 'Israel' },
  { city: 'Amman', country: 'Jordan' },
  { city: 'Beirut', country: 'Lebanon' },
  { city: 'Istanbul', country: 'Turkey' },
  { city: 'Ankara', country: 'Turkey' },
  { city: 'Izmir', country: 'Turkey' },
  
  // Asia
  { city: 'Singapore', country: 'Singapore' },
  { city: 'Hong Kong', country: 'Hong Kong' },
  { city: 'Bangkok', country: 'Thailand' },
  { city: 'Chiang Mai', country: 'Thailand' },
  { city: 'Phuket', country: 'Thailand' },
  { city: 'Pattaya', country: 'Thailand' },
  { city: 'Kuala Lumpur', country: 'Malaysia' },
  { city: 'Penang', country: 'Malaysia' },
  { city: 'Jakarta', country: 'Indonesia' },
  { city: 'Bali', country: 'Indonesia' },
  { city: 'Manila', country: 'Philippines' },
  { city: 'Cebu', country: 'Philippines' },
  { city: 'Ho Chi Minh City', country: 'Vietnam' },
  { city: 'Hanoi', country: 'Vietnam' },
  { city: 'Da Nang', country: 'Vietnam' },
  { city: 'Phnom Penh', country: 'Cambodia' },
  { city: 'Siem Reap', country: 'Cambodia' },
  { city: 'Tokyo', country: 'Japan' },
  { city: 'Osaka', country: 'Japan' },
  { city: 'Kyoto', country: 'Japan' },
  { city: 'Fukuoka', country: 'Japan' },
  { city: 'Seoul', country: 'South Korea' },
  { city: 'Busan', country: 'South Korea' },
  { city: 'Beijing', country: 'China' },
  { city: 'Shanghai', country: 'China' },
  { city: 'Shenzhen', country: 'China' },
  { city: 'Guangzhou', country: 'China' },
  { city: 'Taipei', country: 'Taiwan' },
  { city: 'Mumbai', country: 'India' },
  { city: 'New Delhi', country: 'India' },
  { city: 'Bangalore', country: 'India' },
  { city: 'Chennai', country: 'India' },
  { city: 'Hyderabad', country: 'India' },
  { city: 'Pune', country: 'India' },
  { city: 'Goa', country: 'India' },
  { city: 'Colombo', country: 'Sri Lanka' },
  { city: 'Kathmandu', country: 'Nepal' },
  
  // North America
  { city: 'New York', country: 'USA' },
  { city: 'Los Angeles', country: 'USA' },
  { city: 'San Francisco', country: 'USA' },
  { city: 'Chicago', country: 'USA' },
  { city: 'Miami', country: 'USA' },
  { city: 'Boston', country: 'USA' },
  { city: 'Seattle', country: 'USA' },
  { city: 'Austin', country: 'USA' },
  { city: 'Denver', country: 'USA' },
  { city: 'San Diego', country: 'USA' },
  { city: 'Atlanta', country: 'USA' },
  { city: 'Washington DC', country: 'USA' },
  { city: 'Houston', country: 'USA' },
  { city: 'Dallas', country: 'USA' },
  { city: 'Phoenix', country: 'USA' },
  { city: 'Portland', country: 'USA' },
  { city: 'Las Vegas', country: 'USA' },
  { city: 'Toronto', country: 'Canada' },
  { city: 'Vancouver', country: 'Canada' },
  { city: 'Montreal', country: 'Canada' },
  { city: 'Calgary', country: 'Canada' },
  { city: 'Ottawa', country: 'Canada' },
  { city: 'Mexico City', country: 'Mexico' },
  { city: 'Guadalajara', country: 'Mexico' },
  { city: 'Monterrey', country: 'Mexico' },
  { city: 'Cancun', country: 'Mexico' },
  { city: 'Playa del Carmen', country: 'Mexico' },
  { city: 'Puerto Vallarta', country: 'Mexico' },
  
  // Central & South America
  { city: 'Panama City', country: 'Panama' },
  { city: 'San Jose', country: 'Costa Rica' },
  { city: 'Bogota', country: 'Colombia' },
  { city: 'Medellin', country: 'Colombia' },
  { city: 'Cartagena', country: 'Colombia' },
  { city: 'Lima', country: 'Peru' },
  { city: 'Cusco', country: 'Peru' },
  { city: 'Quito', country: 'Ecuador' },
  { city: 'Santiago', country: 'Chile' },
  { city: 'Buenos Aires', country: 'Argentina' },
  { city: 'Sao Paulo', country: 'Brazil' },
  { city: 'Rio de Janeiro', country: 'Brazil' },
  { city: 'Florianopolis', country: 'Brazil' },
  { city: 'Montevideo', country: 'Uruguay' },
  
  // Oceania
  { city: 'Sydney', country: 'Australia' },
  { city: 'Melbourne', country: 'Australia' },
  { city: 'Brisbane', country: 'Australia' },
  { city: 'Perth', country: 'Australia' },
  { city: 'Adelaide', country: 'Australia' },
  { city: 'Gold Coast', country: 'Australia' },
  { city: 'Auckland', country: 'New Zealand' },
  { city: 'Wellington', country: 'New Zealand' },
  { city: 'Christchurch', country: 'New Zealand' },
  
  // Africa
  { city: 'Cape Town', country: 'South Africa' },
  { city: 'Johannesburg', country: 'South Africa' },
  { city: 'Durban', country: 'South Africa' },
  { city: 'Cairo', country: 'Egypt' },
  { city: 'Casablanca', country: 'Morocco' },
  { city: 'Marrakech', country: 'Morocco' },
  { city: 'Nairobi', country: 'Kenya' },
  { city: 'Lagos', country: 'Nigeria' },
  { city: 'Accra', country: 'Ghana' },
  { city: 'Addis Ababa', country: 'Ethiopia' },
  { city: 'Dakar', country: 'Senegal' },
  { city: 'Tunis', country: 'Tunisia' },
  { city: 'Mauritius', country: 'Mauritius' },
];

const CommunitySelector = ({ user, onCommunityChange, variant = 'navbar' }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [browsingCity, setBrowsingCity] = useState('');
  const [browsingCountry, setBrowsingCountry] = useState('');
  const [residenceCity, setResidenceCity] = useState('');
  const [residenceCountry, setResidenceCountry] = useState('');
  const [isDifferent, setIsDifferent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchBrowsingCommunity();
    }
  }, [user]);

  const fetchBrowsingCommunity = async () => {
    try {
      const response = await axios.get(`${API}/users/me/browsing-community`);
      setBrowsingCity(response.data.browsing_city || '');
      setBrowsingCountry(response.data.browsing_country || '');
      setResidenceCity(response.data.residence_city || '');
      setResidenceCountry(response.data.residence_country || '');
      setIsDifferent(response.data.is_browsing_different || false);
    } catch (error) {
      console.error('Failed to fetch browsing community');
    }
  };

  const handleSelectCommunity = async (city, country) => {
    setLoading(true);
    try {
      await axios.put(`${API}/users/me/browsing-community`, {
        city,
        country
      });
      setBrowsingCity(city);
      setBrowsingCountry(country);
      setIsDifferent(residenceCity ? city !== residenceCity : false);
      setShowDialog(false);
      setSearchQuery('');
      toast.success(`Now browsing ${city}${country ? ', ' + country : ''}`);
      
      // Notify parent component to refresh data
      if (onCommunityChange) {
        onCommunityChange({ city, country });
      }
    } catch (error) {
      console.error('Failed to update community:', error);
      toast.error(error.response?.data?.detail || 'Failed to update community. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToHome = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/users/me/browsing-community`);
      setBrowsingCity(residenceCity);
      setBrowsingCountry(residenceCountry);
      setIsDifferent(false);
      setShowDialog(false);
      toast.success(`Back to ${residenceCity}`);
      
      if (onCommunityChange) {
        onCommunityChange({ city: residenceCity, country: residenceCountry });
      }
    } catch (error) {
      toast.error('Failed to reset community');
    } finally {
      setLoading(false);
    }
  };

  const filteredCities = searchQuery.trim()
    ? POPULAR_CITIES.filter(
        c => 
          c.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.country.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : POPULAR_CITIES;

  if (!user) return null;

  // Navbar variant - compact dropdown
  if (variant === 'navbar') {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className={`flex items-center gap-1 h-8 px-2 rounded-full border border-border/50 bg-muted/30 ${isDifferent ? 'bg-primary/10 text-primary border-primary/30' : ''}`}
              data-testid="community-selector-btn"
            >
              <MapPin className="w-3.5 h-3.5" />
              <span className="max-w-20 truncate text-xs font-medium">
                {browsingCity || 'Select City'}
              </span>
              <ChevronDown className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              Browsing community
            </div>
            <div className="px-2 py-1 font-medium">
              {browsingCity || browsingCountry ? (
                <>
                  {browsingCity}{browsingCity && browsingCountry ? ', ' : ''}{browsingCountry}
                </>
              ) : (
                <span className="text-muted-foreground">Not set</span>
              )}
            </div>
            <DropdownMenuSeparator />
            {isDifferent && residenceCity && (
              <>
                <DropdownMenuItem onClick={handleResetToHome} className="text-primary">
                  <Home className="w-4 h-4 mr-2" />
                  Back to {residenceCity}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => setShowDialog(true)}>
              <Search className="w-4 h-4 mr-2" />
              Browse another city
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <CommunityDialog 
          open={showDialog}
          onClose={() => setShowDialog(false)}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredCities={filteredCities}
          handleSelectCommunity={handleSelectCommunity}
          handleResetToHome={handleResetToHome}
          browsingCity={browsingCity}
          residenceCity={residenceCity}
          residenceCountry={residenceCountry}
          isDifferent={isDifferent}
          loading={loading}
        />
      </>
    );
  }

  // Settings variant - full component
  return (
    <div className="bg-white rounded-xl p-6 border border-border">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <MapPin className="w-5 h-5 text-primary" />
        Browsing Community
      </h3>
      
      <p className="text-sm text-muted-foreground mb-4">
        Choose which city's events and members you want to see. This is useful when traveling or planning a trip.
      </p>

      <div className="space-y-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-muted-foreground mb-1">Currently browsing</div>
          <div className="font-bold text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            {browsingCity || browsingCountry ? (
              <>
                {browsingCity}{browsingCity && browsingCountry ? ', ' : ''}{browsingCountry}
              </>
            ) : (
              <span className="text-muted-foreground">Not set - Select a city below</span>
            )}
            {isDifferent && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                Traveling
              </span>
            )}
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-muted-foreground mb-1">Your residence</div>
          <div className="font-medium flex items-center gap-2">
            <Home className="w-4 h-4" />
            {residenceCity || residenceCountry ? (
              <>
                {residenceCity}{residenceCity && residenceCountry ? ', ' : ''}{residenceCountry}
              </>
            ) : (
              <span className="text-muted-foreground">Not set in profile</span>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={() => setShowDialog(true)}
            className="flex-1 bg-primary hover:bg-primary/90 rounded-full"
          >
            <Search className="w-4 h-4 mr-2" />
            Browse Another City
          </Button>
          {isDifferent && (
            <Button 
              onClick={handleResetToHome}
              variant="outline"
              className="rounded-full"
              disabled={loading}
            >
              <Home className="w-4 h-4 mr-2" />
              Back Home
            </Button>
          )}
        </div>
      </div>

      <CommunityDialog 
        open={showDialog}
        onClose={() => setShowDialog(false)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filteredCities={filteredCities}
        handleSelectCommunity={handleSelectCommunity}
        handleResetToHome={handleResetToHome}
        browsingCity={browsingCity}
        residenceCity={residenceCity}
        residenceCountry={residenceCountry}
        isDifferent={isDifferent}
        loading={loading}
      />
    </div>
  );
};

// Shared dialog component
const CommunityDialog = ({
  open,
  onClose,
  searchQuery,
  setSearchQuery,
  filteredCities,
  handleSelectCommunity,
  handleResetToHome,
  browsingCity,
  residenceCity,
  residenceCountry,
  isDifferent,
  loading
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Browse Another City
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search city or country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-full"
              data-testid="community-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>

          {/* Reset to Home */}
          {isDifferent && (
            <Button
              onClick={handleResetToHome}
              variant="outline"
              className="w-full rounded-full border-primary text-primary hover:bg-primary/10"
              disabled={loading}
            >
              <Home className="w-4 h-4 mr-2" />
              Back to {residenceCity}, {residenceCountry}
            </Button>
          )}

          {/* Cities List */}
          <div className="max-h-64 overflow-y-auto space-y-1">
            {filteredCities.length > 0 ? (
              filteredCities.map((c, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectCommunity(c.city, c.country)}
                  disabled={loading}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center justify-between ${
                    browsingCity === c.city 
                      ? 'bg-primary/10 text-primary' 
                      : 'hover:bg-gray-100'
                  }`}
                  data-testid={`city-option-${c.city.toLowerCase()}`}
                >
                  <div>
                    <div className="font-medium">{c.city}</div>
                    <div className="text-sm text-muted-foreground">{c.country}</div>
                  </div>
                  {browsingCity === c.city && (
                    <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full">
                      Current
                    </span>
                  )}
                </button>
              ))
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <p>No cities found matching "{searchQuery}"</p>
                <p className="text-sm mt-1">Try a different search term</p>
              </div>
            )}
          </div>

          {/* Custom City Input */}
          {searchQuery && !filteredCities.find(c => 
            c.city.toLowerCase() === searchQuery.toLowerCase()
          ) && (
            <div className="border-t pt-4">
              <p className="text-sm text-muted-foreground mb-2">
                Can't find your city? Enter it manually:
              </p>
              <Button
                onClick={() => handleSelectCommunity(searchQuery, '')}
                variant="outline"
                className="w-full rounded-full"
                disabled={loading}
              >
                Browse "{searchQuery}"
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CommunitySelector;
